import {config} from '../../config/app-config';

export function initCarousel(options = {}) {
    const settings = {
        ...config.carousel,
        ...options
    };

    const carouselContainers = document.querySelectorAll('[data-section="carousel"]');

    if (!carouselContainers.length) {
        return;
    }

    carouselContainers.forEach((container, index) => {
        initSingleCarousel(container, settings, index);
    });

    // Send event that all carousels are initialized
    const event = new CustomEvent('all-carousels:initialized');
    document.dispatchEvent(event);
}

function initSingleCarousel(container, settings, carouselIndex) {
    const cards = container.querySelectorAll(settings.selectors.card);
    if (!cards.length) {
        return;
    }

    // Даем ID контейнеру если его нет
    if (!container.id) {
        container.id = `carousel-${carouselIndex}-${Date.now()}`;
    }

    // Determine carousel type
    const isDoctorsCarousel = container.classList.contains('doctors-block') ||
        container.querySelector('.doctor-card') !== null;
    const isDiagnosticAlgorithm = container.classList.contains('diagnostic-alghorithm');

    // Создаем состояние для карусели
    const state = {
        currentPosition: 0,
        visibleCards: 1,
        originalCardWidth: 0,
        gap: settings.cardGap,
        carouselId: container.id,
        isMobile: window.innerWidth < 768,
        isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent) // MOBILE FIX: Определяем iOS
    };

    // Настройки для разных типов каруселей
    if (isDoctorsCarousel) {
        state.gap = 24;
        state.fixedWidth = true;
        // Для мобилок всегда показываем 1 карточку для докторов
        state.visibleCards = state.isMobile ? 1 : 4;
    } else if (isDiagnosticAlgorithm) {
        state.cardWidth = 320;
        state.gap = 24;
        state.fixedWidth = true;
    } else {
        // Обычная карусель
        state.visibleCards = settings.maxVisibleCards || 3;
        state.fixedWidth = false;
    }

    const carouselWrapper = container.closest('.carousel-container') || container.parentElement;

    // Создаем навигацию
    const navigation = createNavigation(settings, carouselWrapper, state.carouselId, {
        isDoctorsCarousel,
        isDiagnosticAlgorithm
    });

    // Настраиваем контейнер и карточки
    setupCarouselContainer(container, cards, state);

    // Вычисляем размеры для докторов
    if (isDoctorsCarousel) {
        calculateDoctorsCardWidth(container, cards, state);
    } else if (state.fixedWidth) {
        // Для диагностических карточек
        const containerWidth = container.parentElement.offsetWidth;
        state.visibleCards = Math.floor(containerWidth / (state.cardWidth + state.gap));
        state.visibleCards = Math.max(1, state.visibleCards);
        state.originalCardWidth = state.cardWidth;
        cards.forEach(card => {
            card.style.width = `${state.cardWidth}px`;
            card.style.minWidth = `${state.cardWidth}px`;
            card.style.maxWidth = `${state.cardWidth}px`;
        });
    } else {
        updateCardSizes(container, cards, settings);
        state.originalCardWidth = cards[0].offsetWidth;
    }

    // Обновляем состояние карусели
    updateCarouselState(container, cards, navigation, state);

    // Устанавливаем обработчики событий
    setupEventListeners(container, cards, navigation, state, settings);

    // Обработчик изменения размера
    const resizeHandler = () => {
        state.isMobile = window.innerWidth < 768;

        // Для докторов на экранах < 768px устанавливаем ширину карточек 70% без пересчета количества
        if (isDoctorsCarousel && state.isMobile) {
            const containerWidth = container.parentElement.offsetWidth;
            state.cardWidth = Math.floor(containerWidth * 0.7); // 70% ширины контейнера
            state.visibleCards = 1; // На мобилке всегда 1 карточка видима
            state.originalCardWidth = state.cardWidth;
            cards.forEach(card => {
                card.style.width = `${state.cardWidth}px`;
                card.style.minWidth = `${state.cardWidth}px`;
                card.style.maxWidth = `${state.cardWidth}px`;
            });
            updateCarouselState(container, cards, navigation, state);
            return;
        }
        if (isDoctorsCarousel) {
            // Пересчитываем размер карточек докторов при ресайзе
            calculateDoctorsCardWidth(container, cards, state);
        } else if (state.fixedWidth) {
            const containerWidth = container.parentElement.offsetWidth;
            const newVisibleCards = Math.floor(containerWidth / (state.cardWidth + state.gap));
            state.visibleCards = Math.max(1, newVisibleCards);
        }
        handleResize(container, cards, navigation, state, settings);
    };

    document.addEventListener('app:resize', resizeHandler);
    window.addEventListener('resize', resizeHandler);

    // Сохраняем обработчик для последующего удаления
    container.setAttribute('data-resize-handler-id', state.carouselId);
    window.carouselResizeHandlers = window.carouselResizeHandlers || {};
    window.carouselResizeHandlers[state.carouselId] = resizeHandler;

    // Отправляем событие инициализации для докторов
    if (isDoctorsCarousel) {
        const event = new CustomEvent('doctors-carousel:initialized', {
            detail: {container, cards}
        });
        document.dispatchEvent(event);
    }
}

// Новая функция для расчета ширины карточек докторов
function calculateDoctorsCardWidth(container, cards, state) {
    const containerWidth = container.parentElement.offsetWidth;
    // Для экранов < 768px ширина карточки 70% от контейнера
    if (window.innerWidth < 768) {
        state.cardWidth = Math.floor(containerWidth * 0.7);
        state.visibleCards = 1; // На мобилке видима только 1 карточка
    } else {
        const targetCards = 4; // Always 4 cards for doctors on desktop
        // Вычисляем доступное пространство с учетом гэпов
        const totalGaps = (targetCards - 1) * state.gap;
        const availableWidth = containerWidth - totalGaps;
        // Вычисляем ширину одной карточки
        state.cardWidth = Math.floor(availableWidth / targetCards);
        state.visibleCards = 4;
    }

    state.originalCardWidth = state.cardWidth;

    // Применяем размеры к карточкам
    cards.forEach(card => {
        card.style.width = `${state.cardWidth}px`;
        card.style.minWidth = `${state.cardWidth}px`;
        card.style.maxWidth = `${state.cardWidth}px`;
    });
}

function setupCarouselContainer(container, cards, state) {
    // Настраиваем контейнер для transform-based карусели
    container.style.display = 'flex';
    container.style.flexWrap = 'nowrap';
    container.style.gap = `${state.gap}px`;
    container.style.transition = 'transform 0.3s ease-out';
    container.style.willChange = 'transform';

    // MOBILE FIX: Добавляем webkit префиксы для iOS
    container.style.webkitTransition = 'transform 0.3s ease-out';
    container.style.webkitTransform = 'translateZ(0)'; // Включаем hardware acceleration

    // Важно: убираем все overflow свойства
    container.style.overflow = 'visible';
    container.style.overflowX = 'visible';
    container.style.overflowY = 'visible';

    // MOBILE FIX: Убираем свойства которые могут вызвать баги на iOS
    container.style.webkitOverflowScrolling = 'auto'; // НЕ touch!
    container.style.scrollSnapType = 'none';
    container.style.msOverflowStyle = 'none';
    container.style.scrollbarWidth = 'none';

    // MOBILE FIX: Предотвращаем баунс эффект на iOS
    if (state.isIOS) {
        container.style.touchAction = 'pan-y pinch-zoom';
    }

    // Настраиваем карточки
    cards.forEach(card => {
        card.style.flexShrink = '0';
        card.style.transition = 'opacity 0.3s ease-out';
        // MOBILE FIX: Hardware acceleration для карточек
        card.style.webkitTransform = 'translateZ(0)';
        card.style.webkitBackfaceVisibility = 'hidden';
    });
}

function createNavigation(settings, container, carouselId, options = {}) {
    let prevBtn, nextBtn;
    const searchRoot = container.parentNode || container;
    const existingNav = searchRoot.querySelector(`.carousel-navigation[data-carousel-id="${carouselId}"]`);

    if (!existingNav) {
        const navigation = document.createElement('div');
        navigation.className = 'carousel-navigation';
        navigation.setAttribute('data-carousel-id', carouselId);

        prevBtn = document.createElement('button');
        prevBtn.className = 'carousel-prev';

        // Устанавливаем правильные aria-labels
        if (options.isDoctorsCarousel) {
            prevBtn.setAttribute('aria-label', 'Previous doctor');
        } else if (options.isDiagnosticAlgorithm) {
            prevBtn.setAttribute('aria-label', 'Previous step');
        } else {
            prevBtn.setAttribute('aria-label', 'Previous slide');
        }

        const prevImage = document.createElement('img');
        prevImage.src = 'images/arrow-left.svg';
        prevImage.alt = '← Back';
        prevBtn.appendChild(prevImage);

        nextBtn = document.createElement('button');
        nextBtn.className = 'carousel-next';

        if (options.isDoctorsCarousel) {
            nextBtn.setAttribute('aria-label', 'Next doctor');
        } else if (options.isDiagnosticAlgorithm) {
            nextBtn.setAttribute('aria-label', 'Next step');
        } else {
            nextBtn.setAttribute('aria-label', 'Next slide');
        }

        const nextImage = document.createElement('img');
        nextImage.src = 'images/arrow-right.svg';
        nextImage.alt = 'Forward →';
        nextBtn.appendChild(nextImage);

        navigation.appendChild(prevBtn);
        navigation.appendChild(nextBtn);

        // Добавляем навигацию после контейнера, а не внутрь
        if (container.parentNode) {
            container.parentNode.insertBefore(navigation, container.nextSibling);
        } else {
            container.appendChild(navigation);
        }
    } else {
        prevBtn = existingNav.querySelector('.carousel-prev');
        nextBtn = existingNav.querySelector('.carousel-next');
    }

    return {prevBtn, nextBtn};
}

function updateCardSizes(container, cards, settings) {
    if (!container || !cards.length) return;

    const containerWidth = container.parentElement.offsetWidth;
    const screenWidth = window.innerWidth;

    // Проверяем размер экрана для планшетов
    let baseMinWidth;
    if (screenWidth >= 768 && screenWidth <= 992) {
        baseMinWidth = 170;
    } else {
        baseMinWidth = settings.minCardWidth || 200;
    }

    const minCardWidth = Math.max(
        baseMinWidth,
        containerWidth / settings.maxVisibleCards - settings.cardGap
    );

    if (screenWidth <= 576) {
        cards.forEach(card => {
            card.style.minWidth = `${minCardWidth}px`;
            card.style.flexBasis = '120px';

        });

    } else if (screenWidth >= 576 && screenWidth <= 768) {
        cards.forEach(card => {
            card.style.minWidth = `${minCardWidth}px`;
            card.style.flexBasis = `170px`;
        });
    } else {
        cards.forEach(card => {
            card.style.minWidth = `${minCardWidth}px`;
            card.style.flexBasis = `${minCardWidth}px`;
        });
    }
}

function updateCarouselState(container, cards, navigation, state) {
    // Для мобилок всегда проверяем реальное количество видимых карточек
    if (state.isMobile) {
        const containerWidth = container.parentElement.offsetWidth;
        const cardWidth = state.originalCardWidth || state.cardWidth || cards[0].offsetWidth;
        const realVisibleCards = Math.floor((containerWidth + state.gap) / (cardWidth + state.gap));
        state.visibleCards = Math.max(1, realVisibleCards);
    }

    const maxPosition = Math.max(0, cards.length - state.visibleCards);
    state.currentPosition = Math.min(Math.max(0, state.currentPosition), maxPosition);

    const offset = state.currentPosition * (state.originalCardWidth + state.gap);

    // MOBILE FIX: Используем webkit префиксы для iOS
    container.style.transform = `translateX(-${offset}px)`;
    container.style.webkitTransform = `translateX(-${offset}px)`;

    // Обновляем видимость карточек для оптимизации
    cards.forEach((card, index) => {
        card.style.removeProperty('opacity');
        const isVisible = index >= state.currentPosition &&
            index < state.currentPosition + state.visibleCards + 1;

        if (isVisible) {
            card.style.opacity = '1';
            card.style.pointerEvents = 'auto';
        }
    });

    // Обновляем состояние кнопок
    navigation.prevBtn.disabled = state.currentPosition === 0;
    navigation.nextBtn.disabled = state.currentPosition >= maxPosition;

    navigation.prevBtn.style.opacity = navigation.prevBtn.disabled ? "0.5" : "1";
    navigation.nextBtn.style.opacity = navigation.nextBtn.disabled ? "0.5" : "1";
    navigation.prevBtn.style.cursor = navigation.prevBtn.disabled ? "default" : "pointer";
    navigation.nextBtn.style.cursor = navigation.nextBtn.disabled ? "default" : "pointer";
}

function setupEventListeners(container, cards, navigation, state, settings) {
    // Hover effect: dim all cards except the hovered one
    cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
            cards.forEach(c => {
                if (c !== card) c.style.setProperty('opacity', '0.4', 'important');
            });
        });
        card.addEventListener('mouseleave', () => {
            cards.forEach(c => c.style.removeProperty('opacity'));
            updateCarouselState(container, cards, navigation, state);
        });
    });

    // Кнопка "Назад"
    navigation.prevBtn.addEventListener('click', () => {
        if (state.currentPosition > 0) {
            state.currentPosition--;
            updateCarouselState(container, cards, navigation, state);
        }
    });

    // Кнопка "Вперед"
    navigation.nextBtn.addEventListener('click', () => {
        const maxPosition = Math.max(0, cards.length - state.visibleCards);
        if (state.currentPosition < maxPosition) {
            state.currentPosition++;
            updateCarouselState(container, cards, navigation, state);
        }
    });

    // Добавляем поддержку клавиатуры
    container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' && !navigation.prevBtn.disabled) {
            navigation.prevBtn.click();
        } else if (e.key === 'ArrowRight' && !navigation.nextBtn.disabled) {
            navigation.nextBtn.click();
        }
    });

    // Добавляем поддержку тач-событий
    setupTouchEvents(container, cards, navigation, state, settings);
}

// MOBILE FIX: Полностью переписанная функция setupTouchEvents для iOS
function setupTouchEvents(container, cards, navigation, state, settings) {
    const isDiagnosticAlgorithm = container.classList.contains('diagnostic-alghorithm');
    const isDoctorsCarousel = container.classList.contains('doctors-block') ||
        container.querySelector('.doctor-card') !== null;

    if (isDiagnosticAlgorithm && window.innerWidth < 768) {
        return;
    }

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let isDragging = false;
    let startTransform = 0;
    let currentTransform = 0;
    let touchStartTime = 0;
    let velocityX = 0;
    let lastTouchX = 0;
    let lastTouchTime = 0;

    // Получаем текущий translateX
    function getCurrentTranslateX() {
        const transform = container.style.transform || container.style.webkitTransform;
        const match = transform.match(/translateX\((-?\d+\.?\d*)px\)/);
        return match ? parseFloat(match[1]) : 0;
    }

    // ВАЖНО: Функция из старого кода для правильного расчета максимального offset
    function getMaxAllowedOffset() {
        const containerWidth = container.parentElement.offsetWidth;
        const lastCard = cards[cards.length - 1];
        const lastCardRight = lastCard.offsetLeft + lastCard.offsetWidth;
        const maxOffset = Math.max(0, lastCardRight - containerWidth);
        return maxOffset;
    }

    // Функция для расчета реального количества видимых карточек
    function getActualVisibleCards() {
        const containerWidth = container.parentElement.offsetWidth;
        const cardWidth = state.originalCardWidth || state.cardWidth;
        const visibleCards = Math.floor((containerWidth + state.gap) / (cardWidth + state.gap));
        return Math.max(1, visibleCards);
    }

    // MOBILE FIX: Отключаем transition через стиль вместо изменения свойства
    function disableTransition() {
        container.style.transition = 'none';
        container.style.webkitTransition = 'none';
    }

    function enableTransition() {
        container.style.transition = 'transform 0.3s ease-out';
        container.style.webkitTransition = 'transform 0.3s ease-out';
    }

    // MOBILE FIX: Добавляем обработчик для предотвращения scroll во время свайпа
    let isHorizontalSwipe = false;

    container.addEventListener('touchstart', (e) => {
        // MOBILE FIX: Сохраняем Y координату для определения направления свайпа
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        lastTouchX = touchStartX;
        lastTouchTime = touchStartTime;

        isDragging = true;
        isHorizontalSwipe = false;
        startTransform = getCurrentTranslateX();
        currentTransform = startTransform;
        velocityX = 0;

        disableTransition();
    }, {passive: true});

    container.addEventListener('touchmove', (e) => {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;

        // MOBILE FIX: Определяем направление свайпа
        const diffX = Math.abs(currentX - touchStartX);
        const diffY = Math.abs(currentY - touchStartY);

        // Если это первое движение, определяем направление
        if (!isHorizontalSwipe && (diffX > 5 || diffY > 5)) {
            isHorizontalSwipe = diffX > diffY;
        }

        // Если это вертикальный свайп, не обрабатываем
        if (!isHorizontalSwipe) {
            isDragging = false;
            enableTransition();
            return;
        }

        // MOBILE FIX: Предотвращаем вертикальный скролл во время горизонтального свайпа
        if (isHorizontalSwipe && state.isIOS) {
            e.preventDefault();
        }

        const deltaX = currentX - touchStartX;
        const newTransform = startTransform + deltaX;
        const maxOffset = getMaxAllowedOffset();

        // ВАЖНО: Ограничиваем transform с учетом реального максимального offset
        if (newTransform > 0) {
            // Эластичность слева
            currentTransform = newTransform * 0.3;
        } else if (Math.abs(newTransform) > maxOffset) {
            // Эластичность справа
            const overflow = Math.abs(newTransform) - maxOffset;
            currentTransform = -(maxOffset + overflow * 0.3);
        } else {
            currentTransform = newTransform;
        }

        // Рассчитываем скорость для инерции
        const currentTime = Date.now();
        const timeDiff = currentTime - lastTouchTime;
        if (timeDiff > 0) {
            velocityX = (currentX - lastTouchX) / timeDiff;
        }
        lastTouchX = currentX;
        lastTouchTime = currentTime;

        // MOBILE FIX: Используем requestAnimationFrame для плавности
        requestAnimationFrame(() => {
            container.style.transform = `translateX(${currentTransform}px)`;
            container.style.webkitTransform = `translateX(${currentTransform}px)`;
        });
    }, {passive: false}); // MOBILE FIX: passive: false для preventDefault

    container.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;

        touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchStartX - touchEndX;

        enableTransition();

        // Получаем актуальное количество видимых карточек для докторов
        let actualVisibleCards = state.visibleCards;
        if (isDoctorsCarousel) {
            const containerWidth = container.parentElement.offsetWidth;
            const cardWidth = state.originalCardWidth || state.cardWidth;
            actualVisibleCards = Math.floor((containerWidth + state.gap) / (cardWidth + state.gap));
            actualVisibleCards = Math.max(1, actualVisibleCards);
        }

        const maxPosition = Math.max(0, cards.length - actualVisibleCards);
        const maxOffset = getMaxAllowedOffset();

        // MOBILE FIX: Учитываем скорость свайпа (инерция)
        const velocityThreshold = 0.3;
        const distanceThreshold = state.isMobile ? 30 : 50;

        // Определяем направление на основе скорости или расстояния
        const shouldSwipeNext = (Math.abs(velocityX) > velocityThreshold && velocityX < 0) ||
                              (Math.abs(swipeDistance) > distanceThreshold && swipeDistance > 0);
        const shouldSwipePrev = (Math.abs(velocityX) > velocityThreshold && velocityX > 0) ||
                              (Math.abs(swipeDistance) > distanceThreshold && swipeDistance < 0);

        if (shouldSwipeNext && state.currentPosition < maxPosition) {
            // Свайп влево - следующая карточка
            state.currentPosition++;
        } else if (shouldSwipePrev && state.currentPosition > 0) {
            // Свайп вправо - предыдущая карточка
            state.currentPosition--;
        }

        // ВАЖНО: Проверка из старого кода для последней карточки
        const cardWidth = state.originalCardWidth || state.cardWidth;
        const desiredOffset = state.currentPosition * (cardWidth + state.gap);

        if (desiredOffset > maxOffset) {
            // Если желаемый offset больше максимального - прижимаем к краю
            state.currentPosition = Math.floor(maxOffset / (cardWidth + state.gap));
            container.style.transform = `translateX(-${maxOffset}px)`;
            container.style.webkitTransform = `translateX(-${maxOffset}px)`;

            // Обновляем состояние кнопок вручную
            navigation.prevBtn.disabled = false;
            navigation.nextBtn.disabled = true;
            navigation.prevBtn.style.opacity = "1";
            navigation.nextBtn.style.opacity = "0.5";
            navigation.prevBtn.style.cursor = "pointer";
            navigation.nextBtn.style.cursor = "default";
        } else {
            // Обычное обновление состояния
            updateCarouselState(container, cards, navigation, {
                ...state,
                visibleCards: actualVisibleCards
            });
        }

        // MOBILE FIX: Сбрасываем флаги
        isHorizontalSwipe = false;
    }, {passive: true});

    // MOBILE FIX: Обработка отмены касания
    container.addEventListener('touchcancel', (e) => {
        if (!isDragging) return;
        isDragging = false;
        isHorizontalSwipe = false;
        enableTransition();

        // Возвращаемся к ближайшей валидной позиции
        const actualVisibleCards = getActualVisibleCards();
        state.visibleCards = actualVisibleCards;
        updateCarouselState(container, cards, navigation, state);
    }, {passive: true});

    // MOBILE FIX: Предотвращаем клики после свайпа
    let preventClick = false;
    container.addEventListener('click', (e) => {
        if (preventClick) {
            e.preventDefault();
            e.stopPropagation();
            preventClick = false;
        }
    }, true);

    container.addEventListener('touchmove', () => {
        preventClick = true;
        setTimeout(() => {
            preventClick = false;
        }, 300);
    }, {passive: true});
}

function handleResize(container, cards, navigation, state, settings) {
    if (!state.fixedWidth) {
        updateCardSizes(container, cards, settings);
        state.originalCardWidth = cards[0].offsetWidth;
    }
    updateCarouselState(container, cards, navigation, state);
}

export function hasCarousel() {
    return document.querySelectorAll('[data-section="carousel"]').length > 0;
}

export function reinitializeCarousel() {
    // Удаляем старые обработчики
    if (window.carouselResizeHandlers) {
        Object.keys(window.carouselResizeHandlers).forEach(id => {
            document.removeEventListener('app:resize', window.carouselResizeHandlers[id]);
            window.removeEventListener('resize', window.carouselResizeHandlers[id]);
        });
    }

    // Очищаем все стили которые могли остаться
    const oldStyles = document.querySelectorAll('style[id*="carousel"]');
    oldStyles.forEach(style => style.remove());

    window.carousel = window.carousel || {};
    window.carousel.initialized = false;
    initCarousel();
}

export function updateStepsNumbers(carouselSection) {
    if (!carouselSection) {
        const carouselSections = document.querySelectorAll('[data-section="carousel"]');
        carouselSections.forEach(section => {
            if (section.classList.contains('diagnostic-alghorithm')) {
                updateStepsNumbersInSection(section);
            }
        });
    } else if (carouselSection.classList.contains('diagnostic-alghorithm')) {
        updateStepsNumbersInSection(carouselSection);
    }
}

function updateStepsNumbersInSection(section) {
    if (!section) {
        return;
    }

    const steps = Array.from(section.querySelectorAll('.diagnostic-step-item'));
    if (!steps.length) return;

    steps.sort((a, b) => {
        const orderA = parseInt(a.getAttribute('data-order') || '0', 10);
        const orderB = parseInt(b.getAttribute('data-order') || '0', 10);
        return orderA - orderB;
    });

    steps.forEach((step, index) => {
        const stepNumber = index + 1;
        const stepNumberElement = step.querySelector('.step-number');
        if (stepNumberElement) {
            stepNumberElement.textContent = `${stepNumber}`;
        }
    });
}

export function setupStepsDragDropNumbersUpdate() {
    document.addEventListener('blockOrderChanged', (event) => {
        const sectionElement = event.detail?.section;
        if (sectionElement && sectionElement.classList.contains('diagnostic-alghorithm')) {
            updateStepsNumbersInSection(sectionElement);
        }
    });

    const carouselSections = document.querySelectorAll('[data-section="carousel"].diagnostic-alghorithm');

    carouselSections.forEach(section => {
        const observer = new MutationObserver((mutations) => {
            updateStepsNumbersInSection(section);
        });

        observer.observe(section, {
            childList: true,
            subtree: false,
            attributes: true,
            attributeFilter: ['data-order']
        });
    });
}

export function initStepsNumbers() {
    updateStepsNumbers();
    setupStepsDragDropNumbersUpdate();
}